import { useCallback, useEffect, useRef, useState } from 'react';
import { coursesApi } from '../services/coursesApi';
import { logger } from '../../../lib/logger';
import {
  isQuizContent,
  isKnowledgeCheckContent,
  isMarkdownContent,
  isHtmlContent,
} from '../types/sectionView.types';

/**
 * Hook for fetching section content via the unified endpoint.
 * Returns pre-loaded data for all content types including quiz/KC.
 *
 * @param {{
 *   sectionId: string | null,
 *   courseId: string,
 *   authEnabled: boolean
 * }} params
 */
export function useSectionView({ sectionId, courseId, authEnabled }) {
  /** @type {[import('../types/sectionView.types').SectionViewResponse | null, Function]} */
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track current fetch to prevent race conditions
  const fetchIdRef = useRef(0);
  const prevSectionIdRef = useRef(null);

  const fetchSection = useCallback(async () => {
    if (!sectionId || !courseId) {
      logger.log('[useSectionView] Missing sectionId or courseId, skipping fetch');
      return;
    }

    // Increment fetch ID to track this specific request
    const currentFetchId = ++fetchIdRef.current;

    logger.log('[useSectionView] Fetching section:', sectionId);
    setIsLoading(true);
    setError(null);

    try {
      const response = await coursesApi.fetchSectionView({
        sectionId,
        courseId,
        auth: authEnabled,
      });

      // Only update state if this is still the current fetch
      if (fetchIdRef.current === currentFetchId) {
        logger.log('[useSectionView] Fetch complete:', response.contentType);
        setData(response);
      } else {
        logger.log('[useSectionView] Stale response ignored for section:', sectionId);
      }
    } catch (err) {
      if (fetchIdRef.current === currentFetchId) {
        const message = err?.message || 'Failed to load section';
        logger.error('[useSectionView] Fetch error:', message);
        setError(message);
      }
    } finally {
      if (fetchIdRef.current === currentFetchId) {
        setIsLoading(false);
      }
    }
  }, [sectionId, courseId, authEnabled]);

  // Fetch when sectionId changes
  useEffect(() => {
    if (sectionId !== prevSectionIdRef.current) {
      prevSectionIdRef.current = sectionId;

      // Clear previous data when switching sections
      if (sectionId) {
        setData(null);
        fetchSection();
      }
    }
  }, [sectionId, fetchSection]);

  // Extract content-type-specific data for convenience
  const content = data?.content || null;

  /** @type {import('../types/sectionView.types').QuizContent | null} */
  const quizData = content && isQuizContent(content) ? content : null;

  /** @type {import('../types/sectionView.types').KnowledgeCheckContent | null} */
  const knowledgeCheckData = content && isKnowledgeCheckContent(content) ? content : null;

  /** @type {string | null} */
  const markdownContent = content && isMarkdownContent(content) ? content.markdown : null;

  /** @type {string | null} */
  const htmlContent = content && isHtmlContent(content) ? content.html : null;

  return {
    // Full response data
    data,

    // Loading/error state
    isLoading,
    error,

    // Refetch function
    refetch: fetchSection,

    // Convenience accessors
    contentType: data?.contentType || null,
    quizData,
    knowledgeCheckData,
    markdownContent,
    htmlContent,

    // Navigation info from response
    navigation: data?.navigation || null,

    // Progress info from response
    progress: data?.progress || null,

    // Section/lesson/course metadata
    sectionInfo: data?.section || null,
    lessonInfo: data?.lesson || null,
    courseInfo: data?.course || null,
  };
}
